import { useEffect, useState } from 'react';

import { Boton, Cargando, Panel } from '../componentes/ui';
import { almacenToken, ErrorApi, pedir } from '../lib/api';
import { baseApi } from '../lib/servidor';
import { useRecurso } from '../lib/consultas';

/**
 * Probar un aparato y ver qué ha reportado, desde el portal.
 *
 * Hasta ahora, cuando una reja no abría, la única herramienta era entrar por SSH
 * a mirar registros. Quien instala está de pie en la caseta con el teléfono en
 * la mano: lo que necesita es mandarle una orden a la pieza que tiene delante y
 * ver si contesta.
 *
 * Vive aparte de la pantalla de topología porque son dos cosas distintas —una
 * es el inventario, la otra es el banco de pruebas— y porque aquella ya lleva
 * cuatro niveles de recorrido y dos formularios.
 */

/** Lo que la ficha del aparato le pasa: lo justo para saber qué se le puede pedir. */
export interface AparatoProbable {
  id: string;
  tipo: string;
  carril: { id: string; nombre: string } | null;
  topicos: Record<string, string> | null;
}

interface Resultado {
  ok: boolean;
  texto: string;
}

export function Probar({ d }: { d: AparatoProbable }) {
  const [ocupado, setOcupado] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);

  async function ordenar(cuerpo: Record<string, unknown>) {
    setOcupado(true);
    setResultado(null);
    try {
      const r = await pedir<{ acusado: boolean; respuesta?: Record<string, unknown> }>(
        `/diagnostico/dispositivos/${d.id}/comando`,
        { metodo: 'POST', cuerpo },
      );
      // «Acusó» y «funcionó» no son lo mismo, y juntarlos escondería el caso
      // más interesante: el aparato que contesta para decir que no pudo.
      const res = String(r.respuesta?.['resultado'] ?? '');
      setResultado(
        r.acusado
          ? { ok: res === 'ok', texto: res === 'ok' ? 'Contestó que sí' : `Contestó: ${res}` }
          : { ok: false, texto: 'No contestó. ¿Está encendido y en la red?' },
      );
    } catch (e) {
      setResultado({ ok: false, texto: e instanceof ErrorApi ? e.message : 'No se pudo mandar' });
    } finally {
      setOcupado(false);
    }
  }

  async function abrirPluma() {
    if (!d.carril) return;
    // Se pregunta porque **sube la barrera de verdad**. Un simulacro sería más
    // cómodo y mentiría sobre lo único que aquí importa comprobar.
    if (
      !window.confirm(
        'Esto sube la barrera de verdad. Comprueba que no haya nadie debajo. ¿Continuar?',
      )
    )
      return;
    setOcupado(true);
    setResultado(null);
    try {
      const r = await pedir<{ acusado?: boolean; exitosa?: boolean; error?: string }>(
        `/diagnostico/carriles/${d.carril.id}/abrir`,
        { metodo: 'POST' },
      );
      setResultado(
        r.acusado
          ? {
              ok: Boolean(r.exitosa),
              texto: r.exitosa ? 'Abrió' : `No pudo abrir: ${r.error ?? 'sin detalle'}`,
            }
          : { ok: false, texto: 'Sin respuesta del controlador' },
      );
    } catch (e) {
      setResultado({ ok: false, texto: e instanceof ErrorApi ? e.message : 'No se pudo abrir' });
    } finally {
      setOcupado(false);
    }
  }

  // Cada aparato tiene lo suyo. Enseñar botones que ese equipo no entiende sería
  // peor que no tenerlos: se prueban, no contestan, y parece que está caído.
  const acciones: { etiqueta: string; correr: () => void; peligrosa?: boolean }[] = [];

  if (d.tipo === 'CONTROLADOR_PLUMA' && d.carril) {
    acciones.push({ etiqueta: 'Abrir la pluma', correr: () => void abrirPluma(), peligrosa: true });
  }

  if (d.tipo === 'KIOSCO') {
    acciones.push(
      {
        etiqueta: 'Foto de la credencial',
        correr: () => void ordenar({ accion: 'captura_prueba', que: 'credencial' }),
      },
      {
        etiqueta: 'Foto del rostro',
        correr: () => void ordenar({ accion: 'captura_prueba', que: 'rostro' }),
      },
      {
        etiqueta: 'Mandar un mensaje',
        correr: () => {
          const texto = window.prompt('Que le diga al visitante:', 'Pase a caseta, por favor');
          if (texto && texto.trim()) {
            void ordenar({ accion: 'mensaje', texto: texto.trim(), segundos: 15 });
          }
        },
      },
      {
        etiqueta: 'Recargar el directorio',
        correr: () => void ordenar({ accion: 'recargar' }),
      },
    );
  }

  if (d.topicos) {
    acciones.push({ etiqueta: 'Ping', correr: () => void ordenar({ accion: 'ping' }) });
  }

  return (
    <Seccion titulo="Probar">
      {acciones.length === 0 ? (
        <p className="p-5 text-body-md text-on-surface-variant">{sinPruebas(d.tipo)}</p>
      ) : (
        <div className="space-y-3 p-5">
          <div className="flex flex-wrap gap-2">
            {acciones.map((a) => (
              <Boton
                key={a.etiqueta}
                // `peligro` y no `primario`: esto levanta una barrera física.
                // El color es parte del aviso, igual que la pregunta de antes.
                variante={a.peligrosa ? 'peligro' : 'contorno'}
                disabled={ocupado}
                onClick={a.correr}
              >
                {ocupado ? 'Esperando…' : a.etiqueta}
              </Boton>
            ))}
          </div>

          {resultado && (
            <p
              role="status"
              className={`text-body-md ${resultado.ok ? 'text-success' : 'text-error'}`}
            >
              {resultado.texto}
            </p>
          )}

          {d.tipo === 'CONTROLADOR_PLUMA' && (
            <p className="text-label-sm text-on-surface-variant">
              Abrir desde aquí sube la barrera de verdad, y queda en la bitácora con tu nombre.
            </p>
          )}
        </div>
      )}
    </Seccion>
  );
}

function sinPruebas(tipo: string): string {
  if (tipo === 'LECTOR_FACIAL') {
    return 'La terminal facial no recibe órdenes por el bus: se configura por ISAPI y empuja sus eventos por HTTP. Lo que sí se ve aquí al lado es lo que va reconociendo.';
  }
  if (tipo === 'TELEFONO_IP') {
    return 'El teléfono va por SIP contra Asterisk; se prueba desde la pantalla de Telefonía.';
  }
  return 'Este aparato todavía no tiene pruebas.';
}

/**
 * Lo último que reportó el aparato.
 *
 * Convierte «no abre» en «el controlador no ha dado señales desde las 3 de la
 * mañana», que es una frase con la que se puede hacer algo. Se refresca solo
 * mientras la pantalla está abierta: quien acaba de probar algo quiere ver
 * aparecer su efecto sin recargar.
 */
export function Eventos({ dispositivoId }: { dispositivoId: string }) {
  const [vuelta, setVuelta] = useState(0);
  const { datos, cargando } = useRecurso<{
    enLinea: boolean;
    ultimoLatido: string | null;
    eventos: EventoAparato[];
  }>(`/diagnostico/dispositivos/${dispositivoId}/eventos?v=${vuelta}`);

  useEffect(() => {
    const t = setInterval(() => setVuelta((v) => v + 1), 5000);
    return () => clearInterval(t);
  }, []);

  if (cargando && !datos) {
    return (
      <Seccion titulo="Qué ha reportado">
        <Cargando />
      </Seccion>
    );
  }

  const eventos = datos?.eventos ?? [];

  return (
    <Seccion titulo="Qué ha reportado">
      {eventos.length === 0 ? (
        <p className="p-5 text-body-md text-on-surface-variant">
          Nada todavía. Aquí aparecen sus latidos, sus errores, los pasos que detecta y las pruebas
          que le hagas.
        </p>
      ) : (
        <ul className="divide-y divide-outline-variant">
          {eventos.map((e) => (
            <Fila key={e.id} e={e} />
          ))}
        </ul>
      )}
    </Seccion>
  );
}

interface EventoAparato {
  id: string;
  tipo: string;
  payload: Record<string, unknown> | null;
  ocurridoEn: string;
}

function Fila({ e }: { e: EventoAparato }) {
  const p = e.payload ?? {};
  const captura = p['capturaId'] as string | undefined;
  return (
    <li className="flex items-start justify-between gap-3 px-5 py-3">
      <div className="min-w-0">
        <p className="text-body-md text-on-surface">{ETIQUETA[e.tipo] ?? e.tipo}</p>
        <p className="truncate text-label-sm text-on-surface-variant">{resumir(e.tipo, p)}</p>
        {captura && <FotoDePrueba id={captura} />}
      </div>
      <span className="shrink-0 text-label-sm text-outline">
        {new Date(e.ocurridoEn).toLocaleTimeString('es-MX')}
      </span>
    </li>
  );
}

/**
 * La foto de una captura de prueba, en pequeño y aquí mismo.
 *
 * Se baja con `fetch` y se pinta desde un blob, **no** con la URL en el `src`:
 * la imagen exige sesión y ni una etiqueta `<img>` ni un enlace mandan la
 * cabecera de autorización —el token vive en el almacén del navegador, no en una
 * cookie—. Puesta como enlace daba un 401 al abrirla, que es exactamente el
 * error que se ve cuando algo se sirve protegido y se enlaza como si no.
 *
 * Y va **en línea** en vez de abrirse aparte: lo que se quiere comprobar es el
 * encuadre, y para eso hay que verlo sin salir de la lista. La URL del objeto
 * muere con el componente, así que la foto de una identificación no se queda
 * colgando en el navegador de la caseta.
 */
function FotoDePrueba({ id }: { id: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    let vivo = true;
    let objeto: string | null = null;

    void (async () => {
      try {
        const r = await fetch(`${baseApi()}/api/diagnostico/capturas/${id}`, {
          headers: { Authorization: `Bearer ${almacenToken.leer() ?? ''}` },
        });
        if (!r.ok) {
          // Puede ser simplemente que ya venció: viven dos horas.
          if (vivo) setFallo(true);
          return;
        }
        if (!vivo) return;
        objeto = URL.createObjectURL(await r.blob());
        setUrl(objeto);
      } catch {
        if (vivo) setFallo(true);
      }
    })();

    return () => {
      vivo = false;
      if (objeto) URL.revokeObjectURL(objeto);
    };
  }, [id]);

  if (fallo) {
    return (
      <p className="mt-1 text-label-sm text-outline">
        La foto ya no está. Las capturas de prueba se borran a las dos horas.
      </p>
    );
  }
  if (!url) return <p className="mt-1 text-label-sm text-outline">Cargando la foto…</p>;

  return (
    <img
      src={url}
      alt="Captura de prueba del aparato"
      className="mt-2 max-h-40 rounded-lg border border-outline-variant"
    />
  );
}

const ETIQUETA: Record<string, string> = {
  latido: 'Latido',
  error: 'Error',
  paso: 'Paso de un vehículo',
  prueba: 'Prueba desde el portal',
  captura_prueba: 'Foto de prueba',
  reconocimiento: 'Cara reconocida',
  tamper: 'Manipulación',
  reinicio: 'Reinicio',
};

/** Una línea que se lea de un vistazo, en vez del JSON crudo. */
function resumir(tipo: string, p: Record<string, unknown>): string {
  if (tipo === 'prueba') {
    const orden = (p['orden'] ?? {}) as Record<string, unknown>;
    return `${String(orden['accion'] ?? '')} · ${p['acusado'] ? 'contestó' : 'no contestó'}`;
  }

  if (tipo === 'captura_prueba') {
    const m = (p['metricas'] ?? {}) as Record<string, number>;
    return [
      String(p['que'] ?? ''),
      m['nitidez'] ? `nitidez ${Math.round(m['nitidez'])}` : null,
      // El reflejo sobre el holograma es la causa más común de que el OCR no lea
      // una credencial, y es justo lo que no se aprecia mirando la foto pequeña.
      m['reflejoLocal'] ? `reflejo ${(m['reflejoLocal'] * 100).toFixed(1)}%` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  if (tipo === 'reconocimiento') {
    return p['enrolado'] ? String(p['nombre'] ?? '') : 'no está enrolada en Kodica';
  }

  if (tipo === 'paso') {
    return p['sinSolicitud'] ? 'sin orden previa: ¿pluma levantada a mano?' : 'confirmado';
  }

  const claves = Object.keys(p);
  return claves.length ? claves.map((k) => `${k}: ${String(p[k])}`).join(' · ') : '';
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Panel>
      <div className="border-b border-outline-variant px-5 py-3">
        <h2 className="text-label-bold uppercase tracking-wider text-on-surface-variant">
          {titulo}
        </h2>
      </div>
      {children}
    </Panel>
  );
}
